<p-toast position="bottom-right" [life]="3000" />

<!-- Global session-expired modal — centered, replaces the abrupt redirect on a 401. -->
@if (sessionExpired.expired()) {
  <div class="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
    <div class="session-expired-card">
      <div class="session-expired-icon">
        <i class="pi pi-lock"></i>
      </div>
      <h2 id="session-expired-title" class="session-expired-title">Your session has expired</h2>
      <p class="session-expired-text">
        For your security, you've been signed out due to inactivity. Please sign in again to continue where you left off.
      </p>
      <button type="button" class="session-expired-btn" (click)="reloginAfterSessionExpiry()">
        <i class="pi pi-sign-in"></i>
        <span>Sign in again</span>
      </button>
    </div>
  </div>
}

@if (!isAuthenticated) {
  <router-outlet />
} @else {
  <div class="app-layout">
    <aside class="app-sidebar" [class.app-sidebar-collapsed]="sidebarCollapsed()">
      <div class="app-sidebar-logo">
        <div class="app-sidebar-logo-icon">
          <i class="pi pi-chart-bar"></i>
        </div>
        <div class="app-sidebar-logo-text">
          <div class="app-sidebar-logo-title">APEX</div>
          <div class="app-sidebar-logo-sub">BISTEC Global</div>
        </div>
      </div>
      <nav class="app-sidebar-nav">
        @for (group of menuItems; track group.label) {
          @if (group.visible !== false && group.routerLink && !group.items?.length) {
            <!-- Top-level direct link (no sub-items), e.g. Team Outings -->
            <a
              [routerLink]="group.routerLink"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: false }"
              class="app-nav-group-label-btn app-nav-toplevel-link">
              @if (group.icon) {
                <i [class]="'pi ' + group.icon" class="app-nav-group-icon"></i>
              }
              <span class="app-nav-group-label-text">{{ group.label }}</span>
            </a>
          } @else if (group.visible !== false && group.items?.length) {
            <div class="app-nav-group">
              <button
                type="button"
                class="app-nav-group-label-btn"
                (click)="toggleNavGroup(group.label ?? '')"
                [attr.aria-expanded]="isNavGroupExpanded(group.label ?? '')">
                @if (group.icon) {
                  <i [class]="'pi ' + group.icon" class="app-nav-group-icon"></i>
                }
                <span class="app-nav-group-label-text">{{ group.label }}</span>
                <i
                  class="pi app-nav-group-chevron"
                  [class.pi-chevron-down]="isNavGroupExpanded(group.label ?? '')"
                  [class.pi-chevron-right]="!isNavGroupExpanded(group.label ?? '')"></i>
              </button>
              @if (isNavGroupExpanded(group.label ?? '')) {
                @for (item of group.items; track item.routerLink || item.label) {
                  @if (item.routerLink && item.visible !== false) {
                    <a
                      [routerLink]="item.routerLink"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: false }"
                      class="app-nav-item">
                      @if (item.icon) {
                        <i [class]="'pi ' + item.icon" class="app-nav-item-icon"></i>
                      }
                      <span>{{ item.label }}</span>
                    </a>
                  }
                }
              }
            </div>
          }
        }
      </nav>
      <button
        type="button"
        class="app-sidebar-toggle"
        (click)="toggleSidebar()"
        [attr.aria-label]="sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
        [title]="sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'">
        <i class="pi" [class.pi-chevron-left]="!sidebarCollapsed()" [class.pi-chevron-right]="sidebarCollapsed()"></i>
      </button>
      @if (user) {
        <div class="app-sidebar-user">
          <div class="app-sidebar-user-avatar" [title]="user.displayName">{{ getInitials() }}</div>
          <div class="app-sidebar-user-info">
            <span class="app-sidebar-user-name">{{ displayNameOnly }}</span>
          </div>
          <button
            type="button"
            class="app-sidebar-signout"
            (click)="logout()"
            title="Sign Out"
            aria-label="Sign Out">
            <i class="pi pi-sign-out"></i>
          </button>
        </div>
      }
    </aside>
    <div class="app-main-wrap">
      <header class="app-main-header">
        <h1 class="app-main-header-title">{{ pageTitle() }}</h1>
        <div class="app-main-header-right">
          @if (isOnAllocationPage()) {
            <select
              class="app-header-select"
              [value]="allocationYear()"
              (change)="onAllocationYearChange(+($any($event.target).value))"
              aria-label="Year">
              @for (opt of allocationYearOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationYear()">{{ opt.label }}</option>
              }
            </select>
            <select
              class="app-header-select"
              [value]="allocationMonth()"
              (change)="onAllocationMonthChange(+($any($event.target).value))"
              aria-label="Month">
              @for (opt of allocationMonthOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationMonth()">{{ opt.label }}</option>
              }
            </select>
          }
          @if (isOnForecastPage()) {
            <select
              class="app-header-select"
              [value]="allocationYear()"
              (change)="onAllocationYearChange(+($any($event.target).value))"
              aria-label="Year">
              @for (opt of allocationYearOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationYear()">{{ opt.label }}</option>
              }
            </select>
          }
          @if (isOnFutureAllocationPage()) {
            <select
              class="app-header-select"
              [value]="allocationYear()"
              (change)="onAllocationYearChange(+($any($event.target).value))"
              aria-label="Year">
              @for (opt of allocationYearOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationYear()">{{ opt.label }}</option>
              }
            </select>
          }
          @if (isOnInvoicePlanningGridPage() || isOnContractsSummaryViewPage() || isOnAvailabilityReportPage()) {
            <select
              class="app-header-select"
              [value]="allocationYear()"
              (change)="onAllocationYearChange(+($any($event.target).value))"
              aria-label="Year">
              @for (opt of allocationYearOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationYear()">{{ opt.label }}</option>
              }
            </select>
          }
          @if (pageHeaderService.canShowFilterSelect | async) {
            <select
              class="app-header-select"
              [value]="(pageHeaderService.filterSelectValue | async) ?? ''"
              (change)="pageHeaderService.triggerFilterSelectChange($any($event.target).value || null)"
              aria-label="Filter">
              <option value="">{{ (pageHeaderService.filterSelectPlaceholder | async) || 'All' }}</option>
              @for (opt of (pageHeaderService.filterSelectOptions | async) ?? []; track opt.value) {
                <option [value]="opt.value ?? ''" [selected]="opt.value === (pageHeaderService.filterSelectValue | async)">
                  {{ opt.label }}
                </option>
              }
            </select>
          }
          @if (isOnTimesheetPage() || isOnMyProjectTasksPage()) {
            <select
              class="app-header-select"
              [value]="allocationYear()"
              (change)="onAllocationYearChange(+($any($event.target).value))"
              aria-label="Year">
              @for (opt of allocationYearOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationYear()">{{ opt.label }}</option>
              }
            </select>
            <select
              class="app-header-select"
              [value]="allocationMonth()"
              (change)="onAllocationMonthChange(+($any($event.target).value))"
              aria-label="Month">
              @for (opt of allocationMonthOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === allocationMonth()">{{ opt.label }}</option>
              }
            </select>
          }
          @if (isOnProjectsPage() || isOnProjectsListPage() || isOnEmployeesPage() || isOnCategoriesPage() || isOnClientsPage() || isOnCostCentersPage() || isOnCountiesPage() || isOnCompaniesPage() || isOnTasksPage() || isOnRolesPage() || isOnDesignationsPage() || isOnServiceGroupsPage() || isOnInvoicingPlanPage() || isOnInvoicePlanningGridPage() || isOnContractsMaintenancePage() || isOnContractsSummaryViewPage() || isOnTimesheetDraftsPage() || isOnTimesheetReportPage() || isOnEffortVarianceReportPage() || isOnProjectUtilizationReportPage() || isOnEmployeeProjectsHistoryPage() || isOnOnboardingSubPage() || isOnOffboardingSubPage() || isOnPresalesPage() || isOnOutingsPage() || isOnOutingReportPage() || (isOnAccessMatrixPage() && !!(pageHeaderService.placeholder | async))) {
            <div class="page-header-search">
              <div class="search-box">
                <i class="pi pi-search"></i>
                <input
                  pInputText
                  type="text"
                  [value]="headerSearchText()"
                  (input)="onHeaderSearchInput($event)"
                  [placeholder]="(pageHeaderService.placeholder | async) || 'Search...'"
                  class="search-input"
                  aria-label="Search" />
                @if (headerSearchText()) {
                  <p-button
                    icon="pi pi-times"
                    (onClick)="onHeaderSearchClear()"
                    [text]="true"
                    severity="secondary"
                    size="small"
                    styleClass="search-clear-btn"
                    aria-label="Clear search" />
                }
              </div>
            </div>
          }
          @if (pageHeaderService.canShowExport | async) {
            <p-button
              label="Export Excel"
              icon="pi pi-file-excel"
              (onClick)="pageHeaderService.triggerExport()"
              severity="secondary"
              [outlined]="true"
              [disabled]="(pageHeaderService.exportDisabled | async) ?? true"
              styleClass="btn-export-excel" />
          }
          @if (pageHeaderService.canShowPrimaryAction | async) {
            <p-button
              [label]="(pageHeaderService.primaryButtonLabel | async) || 'Add'"
              [icon]="(pageHeaderService.primaryButtonIcon | async) ?? 'pi pi-plus'"
              (onClick)="pageHeaderService.triggerPrimaryAction()"
              severity="primary" />
          }
          @if (pageHeaderService.canShowFilterToggle | async) {
            <button
              type="button"
              class="app-header-icon-btn"
              [class.filter-toggle-active]="pageHeaderService.currentFiltersVisible"
              (click)="pageHeaderService.toggleFiltersVisible()"
              [attr.aria-label]="pageHeaderService.currentFiltersVisible ? 'Hide filters' : 'Show filters'"
              [title]="pageHeaderService.currentFiltersVisible ? 'Hide filters' : 'Show filters'">
              <i class="pi pi-filter"></i>
            </button>
          }
          <button
            type="button"
            class="app-header-icon-btn"
            (click)="toggleTheme()"
            [attr.aria-label]="darkMode() ? 'Switch to light mode' : 'Switch to dark mode'"
            [title]="darkMode() ? 'Switch to light mode' : 'Switch to dark mode'">
            <i class="pi" [class.pi-moon]="!darkMode()" [class.pi-sun]="darkMode()"></i>
          </button>
        </div>
      </header>
      <main class="app-main">
        <router-outlet />
      </main>
    </div>
  </div>
}
